// CTE-scope resolver: scans a statement's leading WITH clause and extracts
// CteBinding[] for every top-level CTE. Walker emits CTEs in declaration
// order so consumers can enforce "earlier CTE visible to later CTE" by
// index. Nested CTEs (WITH inside a subquery) are NOT returned here —
// they belong to the inner scope assembled by resolveScope.
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Scope Resolver — CTEs.
//
// Algorithm (single pass, no tree-sitter):
//   1. Skip whitespace/comments at the start of the statement.
//   2. Require the first keyword to be `WITH` (any other keyword, quoted
//      identifier, or unknown text → return []).
//   3. Optionally consume `RECURSIVE` (PG / MySQL 8+ / SQLite 3). Sets
//      isRecursive=true for every CTE in this WITH clause.
//   4. Loop: read cte_name → optional `(col_list)` → require `AS` →
//      optional PG `[NOT] MATERIALIZED` → require `(body)` → record
//      binding → if comma follows, continue; else stop.
//
// MSSQL's `WITH (NOLOCK)` table hint never appears at statement start
// (always after a table reference), so a `WITH` followed by `(` with no
// identifier produces [] naturally.

import type { IdentifierRef, ParseOptions, QualifiedName } from '../../sql-grammar/model';
import { buildLineIndex, rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';

import type { CteBinding, ParsedBuffer } from '../types';
import { skipContextFor, type SkipContext } from '../classify/tokenSkip';

import { foldIdentifier, readIdentifier, type ReadIdentResult } from './identifiers';
import { skipParen } from './aliasScope.scanner';

interface ParseCtx {
  readonly source: string;
  readonly options: ParseOptions;
  readonly ctx: SkipContext;
  readonly lineIdx: LineIndex;
  readonly end: number;
}

export function computeCteBindings(
  buffer: ParsedBuffer,
  statementId: string,
): CteBinding[] {
  const stmt = buffer.documentStructure.statements.find((s) => s.id === statementId);
  if (!stmt) return [];
  return computeCteBindingsForRange(buffer, stmt.range.startOffset, stmt.range.endOffset);
}

// eslint-disable-next-line max-lines-per-function
export function computeCteBindingsForRange(
  buffer: ParsedBuffer,
  start: number,
  end: number,
): CteBinding[] {
  const { parsedDocument } = buffer;
  const { source, options } = parsedDocument;

  const pc: ParseCtx = {
    source,
    options,
    ctx: skipContextFor(options),
    lineIdx: buildLineIndex(source),
    end,
  };

  let i = skipWs(pc, start);
  const firstKw = readIdentifier(source, i, pc.end, options);
  if (!firstKw || firstKw.quoted || firstKw.value.toUpperCase() !== 'WITH') return [];
  i = firstKw.end;

  i = skipWs(pc, i);
  const maybeRec = readIdentifier(source, i, pc.end, options);
  let isRecursive = false;
  if (maybeRec && !maybeRec.quoted && maybeRec.value.toUpperCase() === 'RECURSIVE') {
    isRecursive = true;
    i = maybeRec.end;
  }

  const out: CteBinding[] = [];
  while (i < pc.end) {
    const parsed = parseOneCte(pc, i, isRecursive);
    if (!parsed) break;
    out.push(parsed.binding);
    i = skipWs(pc, parsed.endIdx);
    if (pc.source[i] === ',') { i++; continue; }
    break;
  }
  return out;
}

interface ParsedCte {
  readonly binding: CteBinding;
  readonly endIdx: number;
}

// eslint-disable-next-line complexity
function parseOneCte(pc: ParseCtx, start: number, isRecursive: boolean): ParsedCte | null {
  const { source, options, end } = pc;
  let i = skipWs(pc, start);

  const nameIdent = readIdentifier(source, i, end, options);
  if (!nameIdent) return null;
  i = nameIdent.end;

  let columns: readonly IdentifierRef[] | undefined;
  i = skipWs(pc, i);
  if (source[i] === '(') {
    const cols = parseColumnList(pc, i);
    if (!cols) return null;
    columns = cols.columns;
    i = cols.endIdx;
  }

  i = skipWs(pc, i);
  const asKw = readIdentifier(source, i, end, options);
  if (!asKw || asKw.quoted || asKw.value.toUpperCase() !== 'AS') return null;
  i = asKw.end;

  i = skipOptionalMaterialized(pc, i);

  i = skipWs(pc, i);
  if (source[i] !== '(') return null;
  const bodyStart = i;
  const bodyEnd = skipParen(source, i, end, pc.ctx);

  const name: QualifiedName = {
    name: nameIdent.value,
    raw: nameIdent.raw,
    quoted: nameIdent.quoted,
    foldedName: foldIdentifier(nameIdent.value, nameIdent.quoted, options),
  };
  const binding: CteBinding = {
    name,
    ...(columns && columns.length > 0 ? { columns } : {}),
    isRecursive,
    bodyRange: rangeFromOffsets(pc.lineIdx, bodyStart, bodyEnd),
    nameRange: rangeFromOffsets(pc.lineIdx, nameIdent.start, nameIdent.end),
  };
  return { binding, endIdx: bodyEnd };
}

interface ColumnListResult {
  readonly columns: readonly IdentifierRef[];
  readonly endIdx: number;
}

function parseColumnList(pc: ParseCtx, openIdx: number): ColumnListResult | null {
  const { source, options, end } = pc;
  let i = openIdx + 1;
  const columns: IdentifierRef[] = [];

  while (i < end) {
    i = skipWs(pc, i);
    if (source[i] === ')') return { columns, endIdx: i + 1 };
    const ident = readIdentifier(source, i, end, options);
    if (!ident) return null;
    columns.push(buildColumnRef(pc, ident));
    i = ident.end;
    i = skipWs(pc, i);
    if (source[i] === ',') { i++; continue; }
    if (source[i] === ')') return { columns, endIdx: i + 1 };
    return null;
  }
  return null;
}

function buildColumnRef(pc: ParseCtx, ident: ReadIdentResult): IdentifierRef {
  return {
    qualified: {
      name: ident.value,
      raw: ident.raw,
      quoted: ident.quoted,
      foldedName: foldIdentifier(ident.value, ident.quoted, pc.options),
    },
    range: rangeFromOffsets(pc.lineIdx, ident.start, ident.end),
    kind: 'column',
  };
}

// PG allows `AS MATERIALIZED (body)` and `AS NOT MATERIALIZED (body)`.
// Other engines: the hint is ignored if absent. Consume 0, 1, or 2 tokens.
function skipOptionalMaterialized(pc: ParseCtx, start: number): number {
  const { source, options, end } = pc;
  const i = skipWs(pc, start);
  const k1 = readIdentifier(source, i, end, options);
  if (!k1 || k1.quoted) return start;
  const u1 = k1.value.toUpperCase();
  if (u1 === 'MATERIALIZED') return k1.end;
  if (u1 !== 'NOT') return start;
  const after = skipWs(pc, k1.end);
  const k2 = readIdentifier(source, after, end, options);
  if (k2 && !k2.quoted && k2.value.toUpperCase() === 'MATERIALIZED') return k2.end;
  return start;
}

// Skips whitespace and comments ONLY. Never consumes `"…"` / `` `…` `` /
// `[…]` — those are quoted identifiers for the CTE walker, not strings
// (the shared tokenSkip helper would unconditionally treat them as such,
// which would eat a quoted CTE name or column). Mirrors the pattern
// promoted in the aliasScope slice.
function skipWs(pc: ParseCtx, start: number): number {
  const { source, ctx, end } = pc;
  let i = start;
  while (i < end) {
    const c = source[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }
    if (c === '-' && source[i + 1] === '-') {
      let j = i + 2;
      while (j < end && source[j] !== '\n') j++;
      i = j; continue;
    }
    if (ctx.supportsHashComment && c === '#') {
      let j = i + 1;
      while (j < end && source[j] !== '\n') j++;
      i = j; continue;
    }
    if (c === '/' && source[i + 1] === '*') {
      let j = i + 2;
      while (j < end && !(source[j] === '*' && source[j + 1] === '/')) j++;
      if (j < end) j += 2;
      i = j; continue;
    }
    break;
  }
  return i;
}
